import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Message, Conversation, AIModel } from '../types/chat';
import { AI_MODELS } from '../types/chat';
import { detectKeywords, generateResponse } from '../data/mockChatResponses';
import { chatApi } from '../services/api/endpoints/chat';

interface ChatStore {
  // État UI
  isFloating: boolean;
  isOpen: boolean;
  isTyping: boolean;
  isRecording: boolean;
  isOnline: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Conversations et messages
  conversations: Conversation[];
  activeConversationId: string | null;
  selectedModel: AIModel;
  
  // Actions UI
  setFloating: (floating: boolean) => void;
  setOpen: (open: boolean) => void;
  setTyping: (typing: boolean) => void;
  setRecording: (recording: boolean) => void;
  setSelectedModel: (model: AIModel) => void;
  
  // Actions conversations
  createNewConversation: () => void;
  deleteConversation: (id: string) => void;
  setActiveConversation: (id: string) => void;
  fetchConversations: () => Promise<void>;
  
  // Actions messages
  addMessage: (content: string, type: 'user' | 'bot', attachment?: Message['attachment']) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  toggleLike: (messageId: string) => void;
  toggleDislike: (messageId: string) => void;
  
  // Simulation réponse bot
  simulateBotResponse: () => Promise<void>;
}

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      // État initial
      isFloating: true,
      isOpen: false,
      isTyping: false,
      isRecording: false,
      isOnline: navigator.onLine,
      isLoading: false,
      error: null,
      selectedModel: AI_MODELS[0],
      conversations: [{
        id: '1',
        title: 'Nouvelle conversation',
        timestamp: new Date().toISOString(),
        messages: [{
          id: '1',
          sender: 'bot',
          content: "Bonjour ! Je suis Adha, votre assistant. Comment puis-je vous aider aujourd'hui ?",
          timestamp: new Date().toISOString(),
          likes: 0,
          dislikes: 0
        }],
        isActive: true,
        model: AI_MODELS[0],
        context: []
      }],
      activeConversationId: '1',

      // Actions UI
      setFloating: (floating) => set({ isFloating: floating }),
      setOpen: (open) => set({ isOpen: open }),
      setTyping: (typing) => set({ isTyping: typing }),
      setRecording: (recording) => set({ isRecording: recording }),
      setSelectedModel: (model) => set({ selectedModel: model }),

      // Actions conversations
      createNewConversation: () => {
        const store = get();
        const newConversation: Conversation = {
          id: Date.now().toString(),
          title: 'Nouvelle conversation',
          timestamp: new Date().toISOString(),
          messages: [{
            id: '1',
            sender: 'bot',
            content: "Bonjour ! Je suis Adha, votre assistant. Comment puis-je vous aider aujourd'hui ?",
            timestamp: new Date().toISOString(),
            likes: 0,
            dislikes: 0
          }],
          isActive: true,
          model: store.selectedModel,
          context: []
        };

        set(state => ({
          conversations: [...state.conversations, newConversation],
          activeConversationId: newConversation.id
        }));
      },

      deleteConversation: (id) => {
        const isOnline = navigator.onLine;
        
        // Mettre à jour localement immédiatement
        set(state => {
          const newConversations = state.conversations.filter(c => c.id !== id);
          const newActiveId = state.activeConversationId === id 
            ? newConversations[0]?.id || null 
            : state.activeConversationId;
          
          return {
            conversations: newConversations,
            activeConversationId: newActiveId
          };
        });
        
        // Si en ligne, synchroniser avec l'API
        if (isOnline) {
          chatApi.deleteConversation(id).catch(error => {
            console.error('Erreur lors de la suppression de la conversation:', error);
          });
        }
      },

      setActiveConversation: (id) => {
        set({ activeConversationId: id });
      },

      // Actions messages
      addMessage: async (content, type, attachment) => {
        const store = get();
        const activeConversation = store.conversations.find(
          c => c.id === store.activeConversationId
        );

        if (!activeConversation) return;

        // Construire le contexte
        const context = activeConversation.messages
          .slice(-5) // Prendre les 5 derniers messages
          .map(msg => `${msg.sender === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
          .concat(`User: ${content}`);

        const newMessage: Message = {
          id: Date.now().toString(),
          sender: type,
          content,
          timestamp: new Date().toISOString(),
          likes: 0,
          dislikes: 0,
          attachment
        };

        set(state => ({
          conversations: state.conversations.map(conv => 
            conv.id === state.activeConversationId
              ? {
                  ...conv,
                  messages: [...conv.messages, newMessage],
                  context
                }
              : conv
          )
        }));

        // Synchronisation avec API en arrière-plan si c'est un message utilisateur
        if (type === 'user' && navigator.onLine) {
          try {
            // Envoyer le message à l'API sans attendre ou bloquer l'UI
            chatApi.sendMessage({
              content,
              conversationId: activeConversation.id,
              attachments: attachment ? [attachment] : undefined
            }).catch(error => {
              console.log('Synchronisation en arrière-plan échouée:', error);
              // Ne pas perturber l'UI en cas d'erreur d'API
            });
          } catch (error) {
            // Ignorer les erreurs - le comportement UI reste inchangé
            console.log('Erreur lors de la synchronisation avec l\'API:', error);
          }
        }

        if (type === 'user') {
          await get().simulateBotResponse();
        }
      },

      updateMessage: (messageId, updates) => {
        set(state => ({
          conversations: state.conversations.map(conv => 
            conv.id === state.activeConversationId
              ? {
                  ...conv,
                  messages: conv.messages.map(msg =>
                    msg.id === messageId ? { ...msg, ...updates } : msg
                  )
                }
              : conv
          )
        }));
      },

      toggleLike: (messageId) => {
        const isOnline = navigator.onLine;
        
        // Mettre à jour localement immédiatement pour la réactivité UI
        set(state => ({
          conversations: state.conversations.map(conv => 
            conv.id === state.activeConversationId
              ? {
                  ...conv,
                  messages: conv.messages.map(msg =>
                    msg.id === messageId ? { ...msg, likes: (msg.likes || 0) + 1 } : msg
                  )
                }
              : conv
          )
        }));
        
        // Si en ligne, synchroniser avec l'API
        if (isOnline) {
          chatApi.rateMessage(messageId, { likes: 1 }).catch(error => {
            console.error('Erreur lors de l\'envoi du like:', error);
          });
        }
      },

      toggleDislike: (messageId) => {
        const isOnline = navigator.onLine;
        
        // Mettre à jour localement immédiatement
        set(state => ({
          conversations: state.conversations.map(conv => 
            conv.id === state.activeConversationId
              ? {
                  ...conv,
                  messages: conv.messages.map(msg =>
                    msg.id === messageId ? { ...msg, dislikes: (msg.dislikes || 0) + 1 } : msg
                  )
                }
              : conv
          )
        }));
        
        // Si en ligne, synchroniser avec l'API
        if (isOnline) {
          chatApi.rateMessage(messageId, { dislikes: 1 }).catch(error => {
            console.error('Erreur lors de l\'envoi du dislike:', error);
          });
        }
      },

      // Récupérer les conversations depuis l'API
      fetchConversations: async () => {
        set({ isLoading: true, error: null });
        try {
          const isOnline = navigator.onLine;
          set({ isOnline });
          
          if (!isOnline) {
            // Mode hors ligne - utiliser les données du stockage local
            return;
          }
          
          const response = await chatApi.getConversations();
          
          if (response.success && response.data) {
            set({ 
              conversations: response.data.conversations,
              activeConversationId: response.data.conversations[0]?.id || null
            });
          } else {
            set({ error: response.error || 'Erreur lors de la récupération des conversations' });
          }
        } catch (error) {
          console.error('Erreur de récupération des conversations:', error);
          set({ error: 'Erreur de connexion au service de chat', isOnline: false });
        } finally {
          set({ isLoading: false });
        }
      },
      
      // Réponse bot - Tout passe par l'API backend avec fallback mock
      simulateBotResponse: async () => {
        const store = get();
        const conversation = store.conversations.find(
          c => c.id === store.activeConversationId
        );

        if (!conversation) return;

        set({ isTyping: true, error: null });
        try {
          const lastMessage = conversation.messages[conversation.messages.length - 1];
          
          // Vérifier la connexion pour le backend
          const isOnline = navigator.onLine;
          set({ isOnline });
          
          if (isOnline) {
            try {
              // Utiliser l'API backend avec contexte
              const response = await chatApi.sendMessage({
                content: lastMessage.content,
                conversationId: conversation.id,
                attachments: lastMessage.attachment ? [lastMessage.attachment] : undefined
              });
              
              if (response.success && response.data) {
                // Créer un nouveau message bot directement (sans passer par addMessage pour éviter la récursion)
                const botMessage: Message = {
                  id: Date.now().toString(),
                  sender: 'bot',
                  content: response.data.content,
                  timestamp: new Date().toISOString(),
                  likes: 0,
                  dislikes: 0,
                };

                set(state => ({
                  conversations: state.conversations.map(conv => 
                    conv.id === state.activeConversationId
                      ? { ...conv, messages: [...conv.messages, botMessage] }
                      : conv
                  )
                }));
              } else {
                throw new Error(response.error || 'Erreur de réponse du service de chat');
              }
            } catch (error) {
              console.error('Erreur API, basculement en mode hors ligne:', error);
              set({ isOnline: false, error: 'Service de chat indisponible, utilisation du mode hors ligne' });
              
              // Fallback vers le mode hors ligne avec contexte
              await new Promise(resolve => setTimeout(resolve, 1000));
              const keywords = detectKeywords(lastMessage.content);
              const response = generateResponse(keywords);
              
              // Créer un nouveau message bot directement
              const botMessage: Message = {
                id: Date.now().toString(),
                sender: 'bot',
                content: response,
                timestamp: new Date().toISOString(),
                likes: 0,
                dislikes: 0,
              };

              set(state => ({
                conversations: state.conversations.map(conv => 
                  conv.id === state.activeConversationId
                    ? { ...conv, messages: [...conv.messages, botMessage] }
                    : conv
                )
              }));
            }
          } else {
            // Mode hors ligne - utiliser les données mockées avec contexte
            await new Promise(resolve => setTimeout(resolve, 1500));
            const keywords = detectKeywords(lastMessage.content);
            const response = generateResponse(keywords);
            
            // Créer un nouveau message bot directement
            const botMessage: Message = {
              id: Date.now().toString(),
              sender: 'bot',
              content: response,
              timestamp: new Date().toISOString(),
              likes: 0,
              dislikes: 0,
            };

            set(state => ({
              conversations: state.conversations.map(conv => 
                conv.id === state.activeConversationId
                  ? { ...conv, messages: [...conv.messages, botMessage] }
                  : conv
              )
            }));
          }
        } finally {
          set({ isTyping: false });
        }
      }
    }),
    {
      name: 'chat-storage'
    }
  )
);